import { memo, useState } from 'react'
import type { ContatoItem } from '@hooks/useContatos.js'
import { formatTelefone, telefoneParaWhatsApp } from '@hooks/useContatos.js'
import { Badge } from '@components/primitives/Badge.js'
import { cfIconSvgProps } from '@utils/iconSvg.js'

function IconPhone({ size = 18 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z' />
    </svg>
  )
}

function IconChat({ size = 18 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M21 12a8 8 0 0 1-11.6 7.2L4 21l1.8-5.4A8 8 0 1 1 21 12z' />
      <path d='M8.5 12h.01M12 12h.01M15.5 12h.01' />
    </svg>
  )
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  const a = partes[0]?.[0] ?? ''
  const b = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : ''
  return (a + b).toUpperCase()
}

interface Props {
  item: ContatoItem
  onEditar: (item: ContatoItem) => void
  onRemover: (id: number) => Promise<void>
}

export const ContatoCard = memo(function ContatoCard({ item, onEditar, onRemover }: Props) {
  const [confirmando, setConfirmando] = useState(false)
  const wa = item.telefone ? telefoneParaWhatsApp(item.telefone) : null
  const tel = item.telefone?.replace(/\D/g, '') ?? ''

  return (
    <article className='cf3-contato'>
      <div className='cf3-contato__head'>
        <span className='cf3-contato__avatar' aria-hidden>
          {iniciais(item.nome) || '?'}
        </span>
        <div className='cf3-contato__info'>
          <h3 className='cf3-contato__nome'>{item.nome}</h3>
          <div className='cf3-contato__meta'>
            <Badge label={item.papelLabel} variant='default' size='sm' />
            {item.empresa && <span className='cf3-contato__empresa'>{item.empresa}</span>}
            {item.avaliacao && (
              <span
                className='cf3-contato__nota'
                aria-label={`Avaliação ${item.avaliacao} de 5`}
                title={`Avaliação ${item.avaliacao} de 5`}
              >
                {'★'.repeat(item.avaliacao)}
                <span className='cf3-contato__nota-off' aria-hidden>
                  {'★'.repeat(5 - item.avaliacao)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {item.telefone && (
        <div className='cf3-contato__acoes'>
          <a className='cf3-contato__acao' href={`tel:+${tel.startsWith('55') ? tel : `55${tel}`}`}>
            <IconPhone />
            <span>{formatTelefone(item.telefone)}</span>
          </a>
          {wa && (
            <a
              className='cf3-contato__acao cf3-contato__acao--wa'
              href={`https://wa.me/${wa}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <IconChat />
              <span>WhatsApp</span>
            </a>
          )}
        </div>
      )}

      {item.observacoes && <p className='cf3-contato__obs'>{item.observacoes}</p>}

      <div className='cf3-contato__footer'>
        <button type='button' className='cf3-pend-card__action' onClick={() => onEditar(item)}>
          Editar
        </button>
        {!confirmando ? (
          <button
            type='button'
            className='cf3-pend-card__action cf3-pend-card__action--danger'
            onClick={() => setConfirmando(true)}
          >
            Excluir
          </button>
        ) : (
          <>
            <button
              type='button'
              className='cf3-pend-card__action cf3-pend-card__action--danger'
              onClick={() => onRemover(item.id)}
            >
              Confirmar exclusão
            </button>
            <button
              type='button'
              className='cf3-pend-card__action'
              onClick={() => setConfirmando(false)}
            >
              Cancelar
            </button>
          </>
        )}
      </div>
    </article>
  )
})
